<template>
    <app-wrapper :loan="loan">
        <app-header :loan="loan" medium-max-width="max-w-1000">
            <template #navigation>
                <div class="max-w-200 font-zen-dots text-xl text-primary">
                    <AppLogo />
                </div>
            </template>
            <template #top>
                <div class="md:text-center md:w-full">
                    <h4 class="name">Congratulations {{ loan.user.first_name }}!</h4>
                    You are approved for the following terms.
                </div>
            </template>
            <template #bottom-left>
                <div>Loan Amount:</div>
                <div>Payback Period:</div>
                <div>First Payment:</div>
                <div>{{ loan.frequency | ucfirst }} Payments:</div>
            </template>
            <template #bottom-right>
                <div>{{ loan.amount | money(0) }}</div>
                <div>{{ loan.term }} months</div>
                <div>{{ loan.first_payment | money(2) }}</div>
                <div>{{ loan.remaining_payment | money(2) }}</div>
            </template>
        </app-header>
        <app-body medium-max-width="max-w-500" footer>

            <template #main>

                <LoanPaymentInformationModal 
                    v-if="showLoanPaymentInformationModal"
                    :loan="loan" 
                    :show-slot-content="showSlotContent"
                    :use-min-height="false"
                    @selected="paymentTypeSelected">

                    <h4 class="app-body-title title">
                        Final Step: Review and Sign to Receive Your Loan
                    </h4>

                    <div>
                        <template v-if="is_error_received">
                            <p class="text-danger">
                                We are sorry, but we are unable to process your documents at this time.
                                Please try again later or contact us at <a :href="'mailto:' + support_email">{{ support_email }}</a>.
                            </p>
                        </template>
                    </div>

                    <template v-if="loan.documents.length <= 0">
                        <div class="flex items-center bg-gray-50 border border-gray-300 py-4 px-6 rounded-lg my-7">
                            <div class="flex-auto pr-6">
                                <p class="font-medium mb-0.5 leading-snug">Please wait...</p>
                                <p class="font-medium text-sm text-gray-500 leading-snug">While we are preparing your loan agreement.</p>
                            </div>
                            <div class="flex-initial text-2xl">
                                <i class="fas fa-circle-notch text-green-500 fa-spin" />
                            </div>
                        </div>
                    </template>

                    <template v-for="document in loan.documents">
                        <docu-sign-document v-if="document.type === 'docusign'" :loan="loan" :document="document" />
                        <image-document v-else-if="document.type === 'image'" :loan="loan" :document="document" />
                    </template>

                    <small>Your loan will not be finalized until all sections are signed.<br class="block max-[500px]:hidden" /> Takes less than 1 minute. Secure & encrypted.</small>
                    <!-- <button v-if="loan.documents.length > 0" data-testid="finish-application" @click.prevent="goToCheckRequirements" class="w-full button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
                            :class="{ 'opacity-50 cursor-not-allowed': ! $root.user.email_verified_at || ! allDocumentsSigned }">
                        Finish Application
                    </button> -->

                    <div class="mississipi-customers italic mt-10" v-if="isMississippiCustomer">
                        <p><span class="font-semibold">Mississippi Borrowers:</span> Please review the <a href="/files/MS.Credit.Availability.Act.Pamphlet.pdf" target="_blank" class="underline mississipi-doc-link">Mississippi Credit Availability Pamphlet (PDF)</a> before completing your loan. This pamphlet, required by the Mississippi Department of Banking & Consumer Finance, explains your rights, fees, and lender obligations.</p>
                    </div>

                </LoanPaymentInformationModal>
                

            </template>
        </app-body>
    </app-wrapper>
</template>

<script>
    import DocuSignDocument from '../documents/DocuSign';
    import ImageDocument from '../documents/Image';
    import AppLogo from './../../../components/AppLogo';
    import LoanPaymentInformationModal from './../../../components/LoanPaymentInformationModal';
    import { analytics } from '../../../analytics';

    export default {
        mixins: [require('../../../mixins/InteractsWithGtag').default],
        mixins: [require('../../../mixins/PageViewAnalytics').default],

        props: ['loan', 'is_error_received', 'support_email', 'document_signed'],

        components: {
            DocuSignDocument,
            ImageDocument,
            AppLogo,
            LoanPaymentInformationModal,
        },

        data() {
            return {
                isMississippiCustomer: false,
            }
        },

        created() {

            this.isMississippiCustomer = this.loan.user?.state === 'MS';

            if (this.document_signed == true) {
                analytics.docusignEvents.documentSigned({
                    loan_id: this.loan.id,
                    loan_status: this.loan.status,
                    user_id: this.loan.user_id,
                    page_name: this.gTagPageName,
                });
            }
        },

        mounted() {
            console.log(this.loan);
            if (this.allDocumentsSigned) {
                this.goToCheckRequirements();
                return;
            }

            this.intervalId = setInterval(() => {
                if (this.allDocumentsSigned) {
                    this.goToCheckRequirements();
                } else {
                    this.getLoanDocuments();
                }
            }, 5000);

            if(this.loan?.payment_type) {
                this.showSlotContent = true;
            }
        },

        beforeDestroy() {
            clearInterval(this.intervalId);
        },

        data() {
            return {
                intervalId: null,
                gTagPageName: 'SIGN_LOAN_DOCUMENTS',
                showSlotContent: false,
            }
        },

        computed: {
            sentDocuments() {
                return this.loan.documents.filter(document => document.type === 'docusign' && document.status === 'signed');
            },

            hasSentDocuments() {
                return !! this.sentDocuments.length;
            },

            unsignedDocuments() {
                return this.loan.documents.filter(document => document.type === 'docusign' && document.status !== 'completed');
            },

            allDocumentsSigned() {
                return this.loan.documents?.length && !this.unsignedDocuments.length;
            },

            // Show modal when loan has no payment type OR when not all documents are signed
            showLoanPaymentInformationModal() {
                const loanMissingPaymentType = this.loan && !this.loan.payment_type;
                const documentsNotAllSigned = !this.allDocumentsSigned;
                return loanMissingPaymentType || documentsNotAllSigned;
            },
        },

        methods: {
            /**
             * @returns {void}
             */
            goToDocumentSigning() {
                window.location = `/account/loans/${this.loan.id}/documents`;
            },

            /**
             * @returns {void}
             */
            goToEmailVerification() {
                window.location = `/account/verify-email`;
            },

            /**
             * @returns {void}
             */
            goToCheckRequirements() {
                window.location = `/account/loans/${this.loan.id}/check-requirements`;
            },

            paymentTypeSelected() {
                this.showSlotContent = true;
            },

            /**
             * @returns {void}
             */
            async getLoanDocuments() {
                try {
                    const response = await fetch(`/account/loans/${this.loan.id}/get-documents`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error: ${response.status}`);
                    }

                    const data = await response.json();
                    this.loan.documents = data.documents;
                } catch (error) {
                    console.error('Error fetching documents:', error);
                }
            },
        }
    }
</script>
